<template>
  <div class="attribute-option-page">
    <el-card class="option-card" shadow="always">
      <div class="option-card-header">
        <div class="header-info">
          <h3>属性选项管理</h3>
          <p v-if="attributeConfig">属性名称：{{ attributeConfig.attributeName }} | 类型：{{ getAttributeTypeLabel(attributeConfig.attributeType) }}</p>
        </div>
        <div class="header-actions">
          <el-button @click="goBack">返回</el-button>
          <el-button type="primary" @click="openOptionDialog">新增选项</el-button>
        </div>
      </div>

      <!-- 选项列表 -->
      <div class="option-list">
        <el-table :data="optionList" style="width: 100%">
          <el-table-column prop="optionValue" label="选项值" width="150" />
          <el-table-column prop="optionLabel" label="显示名称" width="200" />
          <el-table-column prop="sortOrder" label="排序" width="80" />
          <el-table-column prop="status" label="状态" width="80">
            <template #default="scope">
              <el-tag :type="scope.row.status ? 'success' : 'warning'">
                {{ scope.row.status ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="创建时间" width="180">
            <template #default="scope">
              {{ formatTime(scope.row.createTime) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="148" class-name="op-col">
            <template #default="scope">
              <div class="op-cell">
                <el-button type="primary" link size="small" @click="handleEditOption(scope.row)">编辑</el-button>
                <el-button
                  :type="scope.row.status === 1 ? 'danger' : 'success'"
                  link
                  size="small"
                  @click="handleToggleStatus(scope.row)"
                >{{ scope.row.status === 1 ? '停用' : '启用' }}</el-button>
                <el-button type="danger" link size="small" @click="handleDeleteOption(scope.row)">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 无数据提示 -->
      <div v-if="!optionList.length" class="no-data">
        <el-empty description="暂无选项配置" />
      </div>
    </el-card>

    <!-- 选项配置对话框 -->
    <el-dialog :title="optionDialogTitle" v-model="optionDialogVisible" width="500px">
      <el-form :model="optionForm" :rules="optionRules" ref="optionFormRef" label-width="80px">
        <el-form-item label="选项值" prop="optionValue">
          <el-input v-model="optionForm.optionValue" placeholder="请输入选项值（用于存储）" />
        </el-form-item>
        <el-form-item label="显示名称" prop="optionLabel">
          <el-input v-model="optionForm.optionLabel" placeholder="请输入显示名称" />
        </el-form-item>
        <el-form-item label="排序顺序">
          <el-input-number v-model="optionForm.sortOrder" :min="0" :max="999" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="optionForm.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="optionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmOption">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

const route = useRoute()
const router = useRouter()

// 数据
const configId = ref(route.query.configId)
const attributeConfig = ref(null)
const optionList = ref([])
const optionDialogVisible = ref(false)
const optionForm = ref({
  optionValue: '',
  optionLabel: '',
  sortOrder: 0,
  status: 1,
  configId: configId.value
})
const optionFormRef = ref()
const editingOption = ref(null)

// 表单验证规则
const optionRules = {
  optionValue: [
    { required: true, message: '请输入选项值', trigger: 'blur' }
  ],
  optionLabel: [
    { required: true, message: '请输入显示名称', trigger: 'blur' }
  ]
}

// 计算属性
const optionDialogTitle = computed(() => {
  return editingOption.value ? '编辑选项' : '新增选项'
})

// 方法
const loadAttributeConfig = async () => {
  try {
    const res = await axios.get('/api/meta-category-attribute-config/' + configId.value)
    attributeConfig.value = res.data?.data
  } catch (error) {
    ElMessage.error('加载属性配置失败')
  }
}

const loadOptions = async () => {
  try {
    const res = await axios.get('/api/meta-category-attribute-option/by-config/' + configId.value)
    optionList.value = res.data?.data || []
  } catch (error) {
    ElMessage.error('加载选项失败')
  }
}

const openOptionDialog = () => {
  editingOption.value = null
  optionForm.value = {
    optionValue: '',
    optionLabel: '',
    sortOrder: optionList.value.length,
    status: 1,
    configId: configId.value
  }
  optionDialogVisible.value = true
}

const handleEditOption = (option) => {
  editingOption.value = option
  optionForm.value = { ...option }
  optionDialogVisible.value = true
}

const confirmOption = async () => {
  try {
    await optionFormRef.value.validate()
    
    const payload = { ...optionForm.value }
    if (editingOption.value) {
      payload.id = editingOption.value.id
    }
    
    await axios.post('/api/meta-category-attribute-option/submit', payload)
    ElMessage.success(editingOption.value ? '更新成功' : '添加成功')
    optionDialogVisible.value = false
    loadOptions()
  } catch (error) {
    if (error.response?.data?.msg) {
      ElMessage.error(error.response.data.msg)
    } else {
      ElMessage.error('保存失败')
    }
  }
}

const handleDeleteOption = (option) => {
  ElMessageBox.confirm('删除后不可恢复，是否继续？', '确认删除', { type: 'warning' })
    .then(async () => {
      try {
        await axios.post('/api/meta-category-attribute-option/remove', null, { 
          params: { ids: String(option.id) } 
        })
        ElMessage.success('删除成功')
        loadOptions()
      } catch (error) {
        ElMessage.error('删除失败')
      }
    })
}

const handleToggleStatus = async (row) => {
  if (!row) return
  const action = row.status === 1 ? '停用' : '启用'
  try {
    await axios.post('/api/common/toggleStatus', null, { params: { entity: 'MetaCategoryAttributeOption', id: row.id } })
    ElMessage.success(`${action}成功`)
    loadOptions()
  } catch (e) {
    ElMessage.error(`${action}失败: ${e.response?.data?.msg || e.message}`)
  }
}

const goBack = () => {
  router.go(-1)
}

const getAttributeTypeLabel = (type) => {
  const labels = {
    text: '文本',
    select: '下拉',
    checkbox: '复选',
    number: '数字',
    date: '日期'
  }
  return labels[type] || type
}

const formatTime = (time) => {
  if (!time) return ''
  return new Date(time).toLocaleString()
}

// 生命周期
onMounted(() => {
  if (!configId.value) {
    ElMessage.error('缺少配置ID参数')
    return
  }
  loadAttributeConfig()
  loadOptions()
})
</script>

<style scoped>
.attribute-option-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: calc(100vh - 96px);
}

.option-card {
  max-width: 1000px;
  margin: 0 auto;
}

.option-card :deep(.el-card__body) {
  padding: 24px;
}

.option-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.header-info h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.header-info p {
  margin: 0;
  font-size: 14px;
  color: #606266;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.option-list {
  margin-top: 16px;
}

.no-data {
  margin-top: 60px;
}
</style>
